import type { UpdateTruckInput } from '@chukua/common';
import { Alert, Button, Form, Input } from 'antd';
import { useEffect } from 'react';
import type { TruckClass } from '../reference-data/reference-data.api';
import { TruckCapabilityFields } from './TruckCapabilityFields';
import { TruckParticularsFields } from './TruckParticularsFields';
import type { TruckView } from './trucks.api';

interface TruckDetailsFormProps {
  truck: TruckView;
  truckClasses: TruckClass[];
  canManage: boolean;
  error: string | null;
  saving: boolean;
  onSave: (input: UpdateTruckInput) => Promise<boolean>;
}

function toFormValues(truck: TruckView): UpdateTruckInput {
  return {
    registration: truck.registration,
    classCode: truck.classCode,
    make: truck.make,
    model: truck.model,
    year: truck.year,
    vin: truck.vin,
    engineNumber: truck.engineNumber,
    capacityTonnes: truck.capacityTonnes,
    tankCapacityLitres: truck.tankCapacityLitres,
    hasGenset: truck.hasGenset,
    isBonded: truck.isBonded,
    isHazardousCertified: truck.isHazardousCertified,
    isCrossBorderEquipped: truck.isCrossBorderEquipped,
    gpsDeviceId: truck.gpsDeviceId ?? undefined,
  };
}

/** Every field but the odometer, editable inline for `truck.manage` holders — read-only for everyone else with `fleet.view` (CLAUDE.md: the server remains the authority). */
export function TruckDetailsForm({ truck, truckClasses, canManage, error, saving, onSave }: TruckDetailsFormProps) {
  const [form] = Form.useForm<UpdateTruckInput>();

  useEffect(() => {
    form.setFieldsValue(toFormValues(truck));
  }, [truck, form]);

  async function submit(values: UpdateTruckInput) {
    await onSave(values);
  }

  return (
    <Form<UpdateTruckInput>
      form={form}
      layout="vertical"
      requiredMark={false}
      onFinish={submit}
      disabled={!canManage || saving}
      initialValues={toFormValues(truck)}
    >
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form.Item name="registration" label="Registration" rules={[{ required: true, message: 'Enter a registration' }]}>
        <Input className="font-mono uppercase" />
      </Form.Item>
      <TruckParticularsFields truckClasses={truckClasses} />
      <TruckCapabilityFields />
      {canManage && (
        <Form.Item className="mb-0">
          <Button type="primary" htmlType="submit" loading={saving}>
            Save changes
          </Button>
        </Form.Item>
      )}
    </Form>
  );
}
